import './Services.module.less';

import {
  ActivityLog,
  Event,
  File,
  Payment,
  PaymentClient,
  type Quotable,
  QuoteLine,
  QuoteUsed,
  QuoteUsedClient,
  ServicesRendered,
  type User,
} from '@kalos/kalos-rpc';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@kalos/ui';
import { HomeRepairService } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ListIcon from '@mui/icons-material/List';
import ZoomIn from '@mui/icons-material/ZoomIn';
import { default as IconButton } from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { format } from 'date-fns';
import { type FC, useCallback, useEffect, useMemo, useReducer, useState } from 'react';

import {
  DIAGNOSTIC_SR_STATUS,
  ENDPOINT,
  OPTION_BLANK,
  PAYMENT_COLLECTED_LIST,
  PAYMENT_NOT_COLLECTED_LIST,
  SERVICE_STATUSES,
  SIGNATURE_PAYMENT_TYPE_LIST,
} from '../../../../constants';
import { captureCoarsePosition } from '../../../../tools/geolocation';
import {
  ActivityLogClientService,
  EventClientService,
  FileClientService,
  formatDateTime,
  formatDateTimeDay,
  makeFakeRows,
  QuoteLineClientService,
  S3ClientService,
  ServicesRenderedClientService,
  timestamp,
} from '../../../../tools/helpers';
import {
  type CompletedJobLogStatusChoice,
  getCompletedJobStatus,
} from '../../../utils/getCompletedJobStatus';
import { ConfirmDelete } from '../../ConfirmDelete';
import { FlatRateSheet } from '../../FlatRate';
import { Form } from '../../Form';
import { type Columns, type Data, InfoTable } from '../../InfoTable';
import { Modal } from '../../Modal';
import { PlainForm, type Schema } from '../../PlainForm';
import { QuoteSelector } from '../../QuoteSelector';
import { SectionBar } from '../../SectionBar';
import { SERVICES_RENDERED_PAYMENT_INITIAL } from './constants';
import { ScChecklist } from './ScChecklist';
import { useScChecklist } from './ScChecklist/useScChecklist';
import { ACTIONS, reducer, sortById } from './servicesReducer';
import { statusRowDatetime } from './statusRowDatetime';

const { NO_STATUS, ENROUTE, ON_CALL, ADMIN, PAYMENT, SIGNATURE, COMPLETED, INCOMPLETE, SIGNED_AS } =
  SERVICE_STATUSES;

/** System-generated rows that use the services-rendered edit modal (payment-linked SRs). */
const SYSTEM_GENERATED_EDITABLE_STATUSES = new Set<string>([
  'Payment',
  'Payment Collected',
  'Material Addition',
  'Remainder Payment',
]);

export type ServicesRenderedPaymentType = {
  servicesRenderedId: number;
  servicesRendered: string;
  technicianNotes: string;
  paymentCollected: number;
  amountCollected: number;
  paymentType: string;
  dateProcessed: string;
  paymentId: number;
};
export type PaymentAndSignatureType = {
  signature: string;
  authorizedSignorName: string;
  authorizedSignorRole: string;
  signorNotes: string;
  amountCollected: number;
  date: string;
  paymentType: string;
};
export type PaymentType = {
  paymentCollected: number;
  amountCollected: number;
  date: string;
  paymentType: string;
};
export type SignatureType = {
  signature: string;
  authorizedSignorName: string;
  authorizedSignorRole: string;
  signorNotes: string;
};
export type SavedSignatureType = {
  signatureData: string;
  authorizedSignorName: string;
  authorizedSignorRole: string;
  signorNotes: string;
};

type SingatureError = { key: string; message: string };

interface Props {
  serviceCallId: number;
  loggedUser: User;
  archived: boolean;
  servicesRenderedProp: ServicesRendered[];
  loading: boolean;
  serviceRenderedPayment?: ServicesRenderedPaymentType;
  paidServicesFetch: () => Promise<Payment[]>;
  currentStatus: string;
  updateServicesRenderedProp: (serviceRendered: ServicesRendered[]) => void;
  updateStatus: (status: string, options?: { logDateCompleted?: string }) => void;
  onUpdateMaterials: () => Promise<void>;
  updateServiceRenderedPayment?: (data: ServicesRenderedPaymentType) => void;
  department: string;
  servicesPendingQuotable: Quotable[];
  servicesPendingNewQuotable: Quotable[];
  servicesCreatingQuote: boolean;
  servicesEditing: ServicesRenderedPaymentType;
  onSetServicesPendingQuotable: (payload: Quotable[]) => void;
  onSetServicesPendingNewQuotable: (payload: Quotable[]) => void;
  onSetServicesCreatingQuote: (payload: boolean) => void;
  onSetServicesEditing: (payload: ServicesRenderedPaymentType) => void;
}

const COLUMNS_SERVICES_RENDERED: Columns = [
  { name: 'Date/Time' },
  { name: 'Employee' },
  { name: 'Services Rendered' },
  { name: '' },
  { name: 'Employee Notes' },
];

const COLUMNS_SERVICES_RENDERED_HISTORY: Columns = [
  { name: 'Date/Time' },
  { name: 'Employee' },
  { name: 'Status' },
];

const SCHEMA_SIGNATURE: Schema<SignatureType> = [
  [
    {
      label: 'Signature',
      name: 'signature',
      type: 'signature',
    },
  ],
  [
    {
      label: 'Authorized Signor Name',
      name: 'authorizedSignorName',
      required: true,
    },
    {
      label: 'Authorized Signor Role',
      name: 'authorizedSignorRole',
      required: true,
    },
    {
      label: 'Signor Notes',
      name: 'signorNotes',
      required: true,
      multiline: true,
    },
  ],
];
const SCHEMA_SIGNATURE_SAVED: Schema<SavedSignatureType> = [
  [
    {
      label: 'Authorized Signor Name',
      name: 'authorizedSignorName',
      disabled: true,
    },
    {
      label: 'Authorized Signor Role',
      name: 'authorizedSignorRole',
      disabled: true,
    },
    {
      label: 'Signor Notes',
      name: 'signorNotes',
      multiline: true,
      disabled: true,
    },
  ],
];

const SCHEMA_PAYMENT: Schema<PaymentType> = [
  [
    {
      label: 'Payment Type',
      name: 'paymentType',
      options: [OPTION_BLANK, ...SIGNATURE_PAYMENT_TYPE_LIST],
    },
    {
      label: 'Amount Collected',
      name: 'amountCollected',
      type: 'number',
      startAdornment: '$',
    },
    {
      label: 'Payment Collected?',
      name: 'paymentCollected',
      type: 'checkbox',
    },
    {
      label: 'Date Processed',
      name: 'date',
      type: 'date',
    },
  ],
];
const PAYMENT_SIGNATURE_INITIAL: PaymentAndSignatureType = {
  signature: '',
  authorizedSignorName: '',
  authorizedSignorRole: '',
  signorNotes: '',
  date: timestamp(),
  paymentType: OPTION_BLANK,
  amountCollected: 0,
};

const handleCheckAmountValid = (data: ServicesRenderedPaymentType) => {
  const errors: { [key: string]: string } = {};
  if (data.paymentCollected && data.amountCollected === 0) {
    errors.amountCollected = 'Enter a non-zero amount when payment collected is checked.';
  }

  return Object.keys(errors).length ? errors : undefined;
};

export const Services: FC<Props> = ({
  serviceCallId,
  loggedUser,
  servicesRenderedProp,
  loading,
  serviceRenderedPayment,
  archived,
  currentStatus,
  paidServicesFetch,
  updateServicesRenderedProp,
  onUpdateMaterials,
  updateStatus,
  updateServiceRenderedPayment,
  department,
  servicesPendingQuotable,
  servicesPendingNewQuotable,
  servicesCreatingQuote,
  servicesEditing,
  onSetServicesPendingQuotable,
  onSetServicesPendingNewQuotable,
  onSetServicesCreatingQuote,
  onSetServicesEditing,
}) => {
  const SIGNATURE_INITIAL: SignatureType = useMemo(
    () => ({
      signature: '',
      authorizedSignorName: loggedUser.firstname + ' ' + loggedUser.lastname,
      authorizedSignorRole: '',
      signorNotes: '',
    }),
    [loggedUser.firstname, loggedUser.lastname],
  );

  const [state, dispatch] = useReducer(reducer, {
    paymentForm: PAYMENT_SIGNATURE_INITIAL,
    viewPayment: undefined,
    viewSignature: undefined,
    signatureForm: SIGNATURE_INITIAL,
    servicesRendered: sortById(servicesRenderedProp),
    deleting: undefined,
    saving: false,
    editing: servicesEditing,
    serviceRenderedPayment: serviceRenderedPayment || SERVICES_RENDERED_PAYMENT_INITIAL,
    changingStatus: false,
    pendingQuotable: servicesPendingQuotable,
    pendingNewQuotable: servicesPendingNewQuotable,
    creatingQuote: servicesCreatingQuote,
  });

  const [isRenderedPaymentValid, setIsRenderedPaymentValid] = useState<boolean>(true);
  const [isRenderedPaymentEditValid, setIsRenderedPaymentEditValid] = useState<boolean>(true);
  const [signatureFormErrors, setSignatureFormErrors] = useState<SingatureError[]>([]);

  // Mounted for every job. Jobs that are not ServiceChannel jobs report
  // notApplicable, so nothing renders and completion is never gated.
  const scChecklist = useScChecklist(serviceCallId, loggedUser.id);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const bucket = 'testbuckethelios';

  // Add this useEffect for debugging
  useEffect(() => {
    console.log('creatingQuote state changed:', state.creatingQuote);
  }, [state.creatingQuote]);

  const hasEditingAccess = useMemo(() => {
    return !!loggedUser.permissionGroups.find(
      ({ name }) => name === 'Manager' || name === 'ServiceCallBillingAccess',
    );
  }, [loggedUser.permissionGroups]);

  const hasServiceCallAdmin = useMemo(
    () => !!loggedUser.permissionGroups?.find((p) => p.name === 'ServiceCallAdmin'),
    [loggedUser.permissionGroups],
  );

  const SCHEMA_ON_CALL: Schema<ServicesRenderedPaymentType> = [
    [
      {
        label: 'Services Rendered',
        name: 'servicesRendered',
        multiline: true,
      },
      {
        name: 'servicesRenderedId',
        type: 'hidden',
      },
      {
        label: 'Employee Notes',
        name: 'technicianNotes',
        multiline: true,
        helperText: 'For internal use',
      },
    ],
    [
      {
        label: 'Payment Collected',
        name: 'paymentCollected',
        type: 'checkbox',
      },

      {
        label: 'Id',
        name: 'paymentId',
        type: 'hidden',
      },
      {
        label: 'Payment Type',
        name: 'paymentType',
        options: [
          OPTION_BLANK,
          ...(servicesEditing?.paymentCollected || state.serviceRenderedPayment.paymentCollected
            ? PAYMENT_COLLECTED_LIST
            : PAYMENT_NOT_COLLECTED_LIST),
        ],
      },
      {
        label: 'Amount Collected',
        name: 'amountCollected',
        type: 'number',
        startAdornment: '$',
      },
      {
        label: 'Date Processed',
        name: 'dateProcessed',
        type: 'date',
      },
    ],
  ];

  const [flatRateModalOpen, setFlatRateModalOpen] = useState(false);
  const [adminCompleteChoiceDialogOpen, setAdminCompleteChoiceDialogOpen] = useState(false);

  const handleOpenFlatRates = useCallback(() => {
    console.log(
      'Opening Flat Rates with department:',
      department,
      'parsed as:',
      department ? parseInt(department, 10) : undefined,
    );
    setFlatRateModalOpen(true);
  }, [department]);

  const handleCloseFlatRates = useCallback(() => {
    setFlatRateModalOpen(false);
  }, []);

  // Add a useEffect to log when flatRateModalOpen changes
  useEffect(() => {
    if (flatRateModalOpen) {
      console.log('Opening FlatRateSheet with department:', department);
    }
  }, [flatRateModalOpen, department]);

  const handleDeleting = useCallback(
    (deleting?: ServicesRendered) => () => dispatch({ type: ACTIONS.SET_DELETING, data: deleting }),
    [],
  );
  const handleDelete = useCallback(async () => {
    if (state.deleting) {
      dispatch({ type: ACTIONS.SET_SAVING, data: true });
      const req = ServicesRendered.create();
      req.id = state.deleting.id;
      const paymentClientService = new PaymentClient(ENDPOINT);
      const paymentReq = Payment.create();
      paymentReq.servicesRenderedId = state.deleting.id;
      try {
        const foundPayment = await paymentClientService.Get(paymentReq);
        if (foundPayment) {
          await paymentClientService.Delete(foundPayment);
          //just to keep it safe, we will refetch the payments
        }
      } catch {
        console.log('no payment found');
      }
      //update the job status to the most recent SR status

      await ServicesRenderedClientService.Delete(req);
      if (onUpdateMaterials) {
        onUpdateMaterials();
      }

      const servicesRendered = state.servicesRendered.filter((el) => el.id != req.id);
      await paidServicesFetch();
      updateServicesRenderedProp(servicesRendered);
      dispatch({ type: ACTIONS.SET_SERVICES_RENDERED, data: servicesRendered });
      dispatch({ type: ACTIONS.SET_SAVING, data: false });
      dispatch({ type: ACTIONS.SET_DELETING, data: undefined });
    }
  }, [
    state.deleting,
    paidServicesFetch,
    state.servicesRendered,
    onUpdateMaterials,
    updateServicesRenderedProp,
  ]);

  const handleSavePendingQuotable = useCallback(
    async (servicesRenderedId: number) => {
      const tempPendingQuotable = servicesPendingQuotable;
      const tempPendingNewQuotable = servicesPendingNewQuotable;
      const quoteUsedClientService = new QuoteUsedClient(ENDPOINT);
      if (tempPendingQuotable.length > 0 || tempPendingNewQuotable.length > 0) {
        for (let i = 0; i < tempPendingQuotable.length; i++) {
          const quotePart = tempPendingQuotable[i];
          const req = QuoteUsed.create();
          req.quoteLineId = quotePart.quoteLineId;
          req.quotedPrice = quotePart.quotedPrice;
          req.quantity = quotePart.quantity;
          req.servicesRenderedId = servicesRenderedId;
          req.billable = quotePart.isBillable === true ? 1 : 0;

          try {
            await quoteUsedClientService.Create(req);
          } catch {
            console.log('failed to add quotable item');
          }
        }
        for (let i = 0; i < tempPendingNewQuotable.length; i++) {
          const quotePart = tempPendingNewQuotable[i];
          const req = QuoteUsed.create();
          const quotelineReq = QuoteLine.create();
          quotelineReq.description = quotePart.description;
          quotelineReq.adjustment = quotePart.quotedPrice.toString();
          quotelineReq.warranty = 2;
          const quotelineRes = await QuoteLineClientService.Create(quotelineReq);
          req.quoteLineId = quotelineRes!.id;
          req.quotedPrice = quotePart.quotedPrice;
          req.quantity = quotePart.quantity;
          req.lmpc = quotePart.isLmpc === true ? 1 : 0;
          req.servicesRenderedId = servicesRenderedId;
          req.billable = quotePart.isBillable == true ? 1 : 0;

          try {
            await quoteUsedClientService.Create(req);
          } catch {
            console.log('failed to add quotable item');
          }
        }
        onSetServicesPendingNewQuotable([]);
        onSetServicesPendingQuotable([]);
      }
    },
    [
      servicesPendingQuotable,
      servicesPendingNewQuotable,
      onSetServicesPendingQuotable,
      onSetServicesPendingNewQuotable,
    ],
  );
  // Helper function to identify system-generated entries
  const isSystemGeneratedEntry = useCallback((sr: ServicesRendered): boolean => {
    return (
      // Approved Proposal entries
      sr.status === 'Approved Proposal' ||
      // Material Addition entries
      sr.status === 'Material Addition' ||
      // Payment entries - ignore ALL payment status entries
      sr.status === 'Payment' ||
      sr.status === 'Payment Collected' ||
      // Remainder Payment entries
      sr.status === 'Remainder Payment' ||
      // Diagnostic entries - legacy diagnosis notes, not part of the tech workflow
      sr.status === DIAGNOSTIC_SR_STATUS
    );
  }, []);

  const getLastStatusForLoggedUser = useCallback(() => {
    let latestSr = ServicesRendered.create();
    for (let i = 0; i < state.servicesRendered.length; i++) {
      const sr = state.servicesRendered[i];
      if (state.servicesRendered[i]?.technicianUserId == loggedUser.id) {
        // Skip system-generated entries from affecting status flow
        if (isSystemGeneratedEntry(sr)) {
          continue;
        }
        if (latestSr.id < sr.id) {
          latestSr = sr;
        }
      }
    }
    if (latestSr.id != 0) {
      return latestSr;
    } else {
      return undefined;
    }
  }, [loggedUser.id, state.servicesRendered, isSystemGeneratedEntry]);

  let lastStatus = NO_STATUS;
  const lastSr = getLastStatusForLoggedUser();
  if (lastSr) {
    lastStatus = lastSr.status;
  }

  if (lastStatus.startsWith(SIGNED_AS)) {
    lastStatus = SIGNED_AS;
  }

  const createActivity = useCallback(
    (message: string) => {
      const activity = ActivityLog.create();
      activity.eventId = serviceCallId;
      activity.activityName = message;
      activity.activityDate = timestamp();
      activity.userId = loggedUser.id;
      return activity;
    },
    [loggedUser.id, serviceCallId],
  );

  const handleCreateStatusActivity = useCallback(
    async ({ lastStatus, newStatus }: { lastStatus: string; newStatus: string }) => {
      const message = `User ${loggedUser.firstname + ' ' + loggedUser.lastname} changed status from '${lastStatus}' to '${newStatus}'`;
      const activity = createActivity(message);
      await ActivityLogClientService.Create(activity);
    },
    [createActivity, loggedUser.firstname, loggedUser.lastname],
  );

  const handleChangeStatus = useCallback(
    (status: string, options?: { forceLogJobStatus?: CompletedJobLogStatusChoice }) => async () => {
      const isSignature = status === SIGNED_AS;
      const forceLogJobStatus = options?.forceLogJobStatus;
      if (isSignature) {
        const errors: SingatureError[] = [];
        if (state.signatureForm.authorizedSignorName.length === 0) {
          errors.push({
            key: 'Authorized Signor Name: ',
            message: 'This field is required',
          });
        }
        if (state.signatureForm.authorizedSignorRole.length === 0) {
          errors.push({
            key: 'Authorized Signor Role: ',
            message: 'This field is required',
          });
        }
        if (state.signatureForm.signorNotes.length === 0) {
          errors.push({
            key: 'Signor Notes: ',
            message: 'This field is required',
          });
        }
        if (state.signatureForm.authorizedSignorName.length > 256) {
          errors.push({
            key: 'Authorized Signor Name: ',
            message: 'This field must be less than 256 characters',
          });
        }
        if (state.signatureForm.authorizedSignorRole.length > 128) {
          errors.push({
            key: 'Authorized Signor Role: ',
            message: 'This field must be less than 128 characters',
          });
        }
        if (state.signatureForm.signorNotes.length > 1024) {
          errors.push({
            key: 'Signor Notes: ',
            message: 'This field must be less than 1024 characters',
          });
        }
        if (errors.length > 0) {
          setSignatureFormErrors(errors);
          return;
        }
      }

      // A ServiceChannel job cannot be completed until its compliance checklist
      // is answered — the subscriber treats the job as unfinished without it,
      // and an unanswered checklist blocks their invoice.
      //
      // Note this refuses on "not loaded" and "failed to load" as well as on
      // genuinely missing answers. Those are not the same as "no checklist
      // required": letting a tech complete a job whose checklist we could not
      // fetch is exactly the failure this gate exists to prevent. Jobs with no
      // checklist report notApplicable and never reach here.
      if (status === COMPLETED && !scChecklist.gate.notApplicable && !scChecklist.gate.complete) {
        setSignatureFormErrors([
          {
            key: 'ServiceChannel checklist: ',
            message: scChecklist.gate.failed
              ? 'The checklist could not be loaded, so this job cannot be completed yet. Reload the page and try again.'
              : scChecklist.gate.loaded
                ? `${scChecklist.gate.unanswered.length} required question(s) still unanswered.`
                : 'The checklist is still loading.',
          },
        ]);
        return;
      }

      setSignatureFormErrors([]);

      // Optimistic update immediately - before any await - for statuses that map to job statuses
      if ([ENROUTE, ON_CALL, INCOMPLETE].includes(status)) {
        updateStatus(status);
      } else if (status === COMPLETED) {
        const jobStatusSet = getCompletedJobStatus({
          forceLogJobStatus,
          lastStatus,
        });
        updateStatus(
          jobStatusSet,
          jobStatusSet === 'Completed' ? { logDateCompleted: timestamp() } : undefined,
        );
      }

      dispatch({ type: ACTIONS.SET_CHANGING_STATUS, data: true });
      dispatch({ type: ACTIONS.SET_SAVING, data: true });
      const req = ServicesRendered.create();
      if (isSignature) {
        req.techNotes = state.signatureForm.signorNotes;
      }
      req.datetime = timestamp();
      req.timeStarted = timestamp();
      req.eventId = serviceCallId;
      const statusToSave = `${status}${
        isSignature
          ? ` ${state.signatureForm.authorizedSignorRole || state.paymentForm.authorizedSignorRole}`
          : ''
      }`;
      req.status = statusToSave;
      req.name = isSignature
        ? `${state.signatureForm.authorizedSignorName || state.paymentForm.authorizedSignorName}`
        : `${loggedUser.firstname} ${loggedUser.lastname}`;

      const fieldMask = ['EventId', 'Status', 'Name', 'Datetime'];
      req.fieldMask = fieldMask;

      /*
      if (state.paymentForm != PAYMENT_SIGNATURE_INITIAL) {
        paymentInfo.amountCollected = state.paymentForm.amountCollected;
        paymentInfo.paymentCollected = 1;
        paymentInfo.date = state.paymentForm.date;
        paymentInfo.paymentType = state.paymentForm.paymentType;
      }
      */
      if (state.serviceRenderedPayment !== SERVICES_RENDERED_PAYMENT_INITIAL) {
        //we should update the SR, and include payment if needed
        req.serviceRendered = state.serviceRenderedPayment.servicesRendered;
        req.techNotes = state.serviceRenderedPayment.technicianNotes;
        // Not the form's default Date Processed, which is the page-load time (FU1.2).
        req.datetime = statusRowDatetime(state.serviceRenderedPayment.dateProcessed, timestamp());
      }
      req.technicianUserId = loggedUser.id;
      if (req.datetime == '') {
        req.datetime = timestamp();
      }

      // Where the technician is, when the device will say.
      //
      // ServiceChannel records a visit only if the status change carries a
      // coordinate — universalCheckIn and universalCheckOut both require one,
      // and without it the connector can push the status but nothing proves
      // anyone attended. Today the office reconstructs that from truck
      // tracking screenshots on every commercial job.
      //
      // Awaited rather than fired off, because the coordinate has to be on
      // this row: the connector reads the services_rendered row this call
      // creates. It is bounded at five seconds, usually returns instantly from
      // a cached fix, and resolves to null on every failure — a denied
      // permission or a dead GPS must never stop a technician recording their
      // status.
      const position = await captureCoarsePosition();
      if (position) {
        req.latitude = position.latitude;
        req.longitude = position.longitude;
        req.fieldMask.push('Latitude', 'Longitude');
      }

      const res = await ServicesRenderedClientService.Create(req);
      //now update the most recent one to have a completion date
      if (state.servicesRendered.length > 0) {
        const mostRecent = getLastStatusForLoggedUser();
        if (mostRecent) {
          const updateSr = ServicesRendered.create();
          updateSr.id = mostRecent.id;
          updateSr.timeFinished = timestamp();
          updateSr.fieldMask.push('TimeFinished');
          await ServicesRenderedClientService.Update(updateSr);
        }
      }
      const servicesRendered = state.servicesRendered;
      servicesRendered.push(res!);
      updateServicesRenderedProp(servicesRendered);
      dispatch({ type: ACTIONS.SET_SERVICES_RENDERED, data: servicesRendered });

      if ([ENROUTE, ON_CALL, INCOMPLETE].includes(status)) {
        const updateEvent = Event.create();
        updateEvent.id = serviceCallId;
        updateEvent.logJobStatus = status;
        updateEvent.fieldMask = ['LogJobStatus'];
        await EventClientService.Update(updateEvent);
      }

      if (
        res!.id != 0 &&
        (servicesPendingNewQuotable.length > 0 || servicesPendingQuotable.length > 0)
      ) {
        await handleSavePendingQuotable(res!.id);
      }
      if (isSignature) {
        let tempSignatureData = state.signatureForm.signature;
        if (state.paymentForm.signature != '') {
          tempSignatureData = state.paymentForm.signature;
        }
        const fileReq = File.create();
        fileReq.mimeType = 'image/png';
        fileReq.bucket = bucket;
        const fileName = `signature/${res!.id}-${serviceCallId}-${format(
          new Date(),
          'hhmmss',
        )}.png`;
        try {
          await S3ClientService.uploadFileToS3Bucket(
            fileName,
            tempSignatureData,
            bucket,
            'signature',
          );
        } catch (err) {
          console.log('failed to upload to AWS', err);
        }
        fileReq.name = fileName;
        const fileRes = await FileClientService.Create(fileReq);
        res!.signatureId = fileRes!.id;
        res!.fieldMask = ['SignatureId'];
        await ServicesRenderedClientService.Update(res!);
      }
      if (
        state.serviceRenderedPayment.paymentType != '-- Select --' &&
        state.serviceRenderedPayment.amountCollected !== 0
      ) {
        const paymentReq = Payment.create();
        paymentReq.collected = state.serviceRenderedPayment.paymentCollected;
        paymentReq.amountCollected = state.serviceRenderedPayment.amountCollected;
        paymentReq.type = state.serviceRenderedPayment.paymentType;
        const paymentClientService = new PaymentClient(ENDPOINT);
        paymentReq.servicesRenderedId = res!.id;
        await paymentClientService.Create(paymentReq);
        await paidServicesFetch();
      }

      if (status === COMPLETED) {
        const jobStatusSet = getCompletedJobStatus({
          forceLogJobStatus,
          lastStatus,
        });
        const updateEvent = Event.create();
        updateEvent.id = serviceCallId;
        updateEvent.logJobStatus = jobStatusSet;
        updateEvent.fieldMask.push('LogJobStatus');
        if (jobStatusSet === 'Completed') {
          updateEvent.logDateCompleted = res?.datetime || '';
          updateEvent.fieldMask.push('LogDateCompleted');
        }
        await EventClientService.Update(updateEvent);
      }

      await handleCreateStatusActivity({
        lastStatus,
        newStatus: status,
      });
      //create png image, upload Name of signature is
      //"signature/#form.event_id#-#local.services_rendered_id#-#timeFormat(now(),'hhmmss')#.png"
      //After successful creation,Signature ID=file ID in table
      dispatch({
        type: ACTIONS.SET_PAYMENT_FORM,
        data: PAYMENT_SIGNATURE_INITIAL,
      });

      dispatch({
        type: ACTIONS.SET_SERVICE_RENDERED_PAYMENT,
        data: SERVICES_RENDERED_PAYMENT_INITIAL,
      });
      updateServiceRenderedPayment?.(SERVICES_RENDERED_PAYMENT_INITIAL);

      // Close the materials toolbox and clear any remaining materials after status completion
      onSetServicesCreatingQuote(false);
      onSetServicesPendingQuotable([]);
      onSetServicesPendingNewQuotable([]);

      // Clear any editing state to ensure fresh start for next service round
      const initialEditingState: ServicesRenderedPaymentType = {
        servicesRenderedId: 0,
        servicesRendered: '',
        technicianNotes: '',
        paymentType: OPTION_BLANK,
        amountCollected: 0,
        paymentCollected: 0,
        paymentId: 0,
        dateProcessed: '',
      };
      onSetServicesEditing(initialEditingState);

      dispatch({ type: ACTIONS.SET_SAVING, data: false });
      if (onUpdateMaterials) {
        await onUpdateMaterials();
      }
      dispatch({ type: ACTIONS.SET_SIGNATURE_FORM, data: SIGNATURE_INITIAL });

      dispatch({ type: ACTIONS.SET_CHANGING_STATUS, data: false });
    },
    [
      serviceCallId,
      // The whole gate object, not its fields: a stale closure here would read
      // last render's completeness and let a job through on answers the tech
      // has since cleared.
      scChecklist.gate,
      updateServiceRenderedPayment,
      state.signatureForm.authorizedSignorRole,
      state.signatureForm.authorizedSignorName,
      state.signatureForm.signorNotes,
      state.signatureForm.signature,
      state.paymentForm.authorizedSignorRole,
      state.paymentForm.authorizedSignorName,
      state.paymentForm.signature,
      state.serviceRenderedPayment,
      state.servicesRendered,
      servicesPendingNewQuotable.length,
      servicesPendingQuotable.length,
      loggedUser.firstname,
      loggedUser.lastname,
      loggedUser.id,
      updateServicesRenderedProp,
      onUpdateMaterials,
      updateStatus,
      SIGNATURE_INITIAL,
      getLastStatusForLoggedUser,
      handleSavePendingQuotable,
      paidServicesFetch,
      lastStatus,
      handleCreateStatusActivity,
      onSetServicesCreatingQuote,
      onSetServicesPendingQuotable,
      onSetServicesPendingNewQuotable,
      onSetServicesEditing,
    ],
  );

  const handleChangeServiceRendered = useCallback(
    async (data: ServicesRenderedPaymentType) => {
      if (servicesEditing.servicesRenderedId !== 0) {
        const paymentClientService = new PaymentClient(ENDPOINT);
        dispatch({ type: ACTIONS.SET_SAVING, data: true });
        const srReq = ServicesRendered.create();
        srReq.serviceRendered = data.servicesRendered;
        srReq.techNotes = data.technicianNotes;
        srReq.datetime = data.dateProcessed;
        srReq.id = data.servicesRenderedId;
        srReq.fieldMask = ['TechNotes', 'ServiceRendered', 'Datetime'];
        //update sr
        await ServicesRenderedClientService.Update(srReq);
        const servicesRendered = state.servicesRendered;
        const index = servicesRendered.findIndex((el) => el.id == srReq.id);

        const updateSr = ServicesRendered.create();
        updateSr.id = srReq.id;
        const result = await ServicesRenderedClientService.Get(updateSr);
        if (result) {
          servicesRendered[index] = result;
        }
        updateServicesRenderedProp(servicesRendered);
        dispatch({
          type: ACTIONS.SET_SERVICES_RENDERED,
          data: servicesRendered,
        });

        const paymentReq = Payment.create();
        paymentReq.id = data.paymentId;
        paymentReq.collected = data.paymentCollected;
        paymentReq.amountCollected = data.amountCollected;
        paymentReq.type = data.paymentType;

        //if payment ID, update, else create
        if (paymentReq.id == 0 && paymentReq.amountCollected != 0) {
          paymentReq.servicesRenderedId = srReq.id;
          await paymentClientService.Create(paymentReq);
          await paidServicesFetch();
        } else {
          paymentReq.fieldMask = ['Collected', 'AmountCollected', 'Type'];
          await paymentClientService.Update(paymentReq);
          await paidServicesFetch();
          const activity = createActivity(
            `User ${loggedUser.firstname + ' ' + loggedUser.lastname} changed the service rendered`,
          );
          await ActivityLogClientService.Create(activity);
        }

        dispatch({ type: ACTIONS.SET_SAVING, data: false });
        const init: ServicesRenderedPaymentType = {
          servicesRenderedId: 0,
          servicesRendered: '',
          technicianNotes: '',
          paymentType: OPTION_BLANK,
          amountCollected: 0,
          paymentCollected: 0,
          paymentId: 0,
          dateProcessed: '',
        };
        onSetServicesEditing(init);
      }
    },
    [
      paidServicesFetch,
      createActivity,
      servicesEditing,
      state.servicesRendered,
      updateServicesRenderedProp,
      onSetServicesEditing,
      loggedUser.firstname,
      loggedUser.lastname,
    ],
  );
  const handleSetEditing = useCallback(
    (sr?: ServicesRendered) => async () => {
      const paymentReq = Payment.create();
      const paymentClientService = new PaymentClient(ENDPOINT);
      const temp: ServicesRenderedPaymentType = {
        servicesRenderedId: 0,
        servicesRendered: '',
        technicianNotes: '',
        paymentType: OPTION_BLANK,
        amountCollected: 0,
        paymentCollected: 0,
        paymentId: 0,
        dateProcessed: '',
      };
      if (sr) {
        temp.servicesRendered = sr.serviceRendered;
        temp.servicesRenderedId = sr.id;
        temp.technicianNotes = sr.techNotes;
        temp.dateProcessed = sr.datetime;

        try {
          paymentReq.servicesRenderedId = sr.id;
          const paymentResults = await paymentClientService.Get(paymentReq);
          if (paymentResults) {
            temp.amountCollected = paymentResults.amountCollected;
            temp.paymentCollected = paymentResults.collected;
            temp.paymentId = paymentResults.id;
            temp.paymentType = paymentResults.type;
          }
        } catch {
          console.log('no payment found, stick with default');
        }
      }
      setIsRenderedPaymentEditValid(true);
      onSetServicesEditing(temp);
    },
    [onSetServicesEditing],
  );

  const handleSetViewPreview = useCallback(
    (sr: ServicesRendered, status: string) => async () => {
      if (status === PAYMENT) {
        const paymentClientService = new PaymentClient(ENDPOINT);
        const paymentReq = Payment.create();
        paymentReq.servicesRenderedId = sr.id;
        const paymentResults = await paymentClientService.Get(paymentReq);
        if (paymentResults) {
          dispatch({
            type: ACTIONS.SET_VIEW_PAYMENT,
            data: {
              paymentType: paymentResults.type,
              amountCollected: paymentResults.amountCollected,
              paymentCollected: paymentResults.collected,
              date: format(new Date(), 'yyyy-MM-dd'),
            },
          });
        }
      } else {
        const fileReq = File.create();
        fileReq.id = sr.signatureId;
        const fileRes = await FileClientService.Get(fileReq);
        const s3Data = await S3ClientService.getFileS3BucketUrl(fileRes!.name, fileRes!.bucket);
        dispatch({
          type: ACTIONS.SET_VIEW_SIGNATURE,
          data: {
            signatureData: s3Data,
            signorNotes: sr.techNotes,
            authorizedSignorName: sr.name,
            authorizedSignorRole: sr.status,
          },
        });
      }
    },
    [],
  );
  const data: Data = loading
    ? makeFakeRows(4, 3)
    : state.servicesRendered.map((props) => {
        const isSystemGenerated = isSystemGeneratedEntry(props);
        const hasAdminAccess =
          loggedUser.permissionGroups.find((el) => el.name === 'ServiceCallBillingAccess') ||
          loggedUser.permissionGroups.find((el) => el.name === 'Manager') ||
          loggedUser.permissionGroups.find((el) => el.name === 'ServiceCallAdmin');
        const isEditableSystemGenerated =
          isSystemGenerated && SYSTEM_GENERATED_EDITABLE_STATUSES.has(props.status);
        const canEditSystemGeneratedRow =
          hasEditingAccess || props.technicianUserId === loggedUser.id;

        return [
          { value: formatDateTime(props?.timeStarted || '') },
          { value: props?.name },
          {
            value: (
              <span
                style={{
                  ...([COMPLETED, INCOMPLETE].includes(props?.status)
                    ? {
                        color: props?.status === COMPLETED ? 'green' : 'red',
                      }
                    : {}),
                  ...(isSystemGenerated ? { fontStyle: 'italic', color: '#666' } : {}),
                }}
              >
                {props?.status}
                {isSystemGenerated && ' (System Generated)'}
              </span>
            ),
            actions: isEditableSystemGenerated
              ? [
                  <Tooltip key="edit" title="Edit" placement="top">
                    <IconButton
                      key="edit-sg"
                      onClick={handleSetEditing(props)}
                      size="small"
                      disabled={!canEditSystemGeneratedRow}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>,
                  ...(hasAdminAccess
                    ? [
                        <Tooltip key="delete" title="Delete" placement="top">
                          <IconButton
                            key={0}
                            onClick={handleDeleting(props)}
                            size="small"
                            disabled={archived}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>,
                      ]
                    : []),
                ]
              : isSystemGenerated && !hasAdminAccess
                ? [] // No actions for other system-generated entries unless user has admin access
                : isSystemGenerated && hasAdminAccess
                  ? [
                      <Tooltip key="delete" title="Delete" placement="top">
                        <IconButton
                          key={0}
                          onClick={handleDeleting(props)}
                          size="small"
                          disabled={archived}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>,
                    ]
                  : [
                      ...([COMPLETED, INCOMPLETE].includes(props?.status)
                        ? [
                            <Tooltip key="edit" title="Edit" placement="top">
                              <IconButton
                                key={1}
                                onClick={handleSetEditing(props)}
                                size="small"
                                disabled={
                                  !hasEditingAccess && props.technicianUserId !== loggedUser.id
                                }
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>,
                          ]
                        : []),
                      ...([SIGNED_AS].includes(props?.status)
                        ? [
                            <Tooltip key="zoom" title="Zoom In" placement="top">
                              <IconButton
                                key={2}
                                onClick={handleSetViewPreview(props, SIGNED_AS)}
                                size="small"
                              >
                                <ZoomIn />
                              </IconButton>
                            </Tooltip>,
                          ]
                        : []),
                      ...(props?.status.includes(SIGNED_AS)
                        ? [
                            <Tooltip key="sign" title="View Signature" placement="top">
                              <IconButton
                                key={3}
                                onClick={handleSetViewPreview(props, SIGNED_AS)}
                                size="small"
                              >
                                <ZoomIn />
                              </IconButton>
                            </Tooltip>,
                          ]
                        : []),
                      <Tooltip key="delete" title="Delete" placement="top">
                        <IconButton
                          key={0}
                          onClick={handleDeleting(props)}
                          size="small"
                          disabled={archived}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>,
                    ],
          },
        ];
      });

  const servicesRenderedData: Data = state.servicesRendered
    .filter((sr) => [COMPLETED, INCOMPLETE].includes(sr?.status))
    .map((sr) => [
      { value: formatDateTimeDay(sr.timeStarted) },
      { value: sr.name },
      { value: sr.serviceRendered },
      { value: '' },
      { value: sr.techNotes },
    ]);

  return (
    <>
      {/*
        Above the services-rendered history on purpose: it gates completion, so
        a tech should meet it before the status actions rather than after
        scrolling past them. Renders nothing for non-ServiceChannel jobs.
      */}
      {!scChecklist.gate.notApplicable && (
        <>
          <SectionBar title="ServiceChannel Checklist" />
          <ScChecklist
            checklists={scChecklist.checklists}
            draft={scChecklist.draft}
            gate={scChecklist.gate}
            saving={scChecklist.saving}
            disabled={archived}
            setAnswer={scChecklist.setAnswer}
            onSave={scChecklist.save}
          />
        </>
      )}
      {servicesRenderedData.length > 0 && (
        <>
          <SectionBar
            key={state.servicesRendered.toString() + 'Header'}
            title="Services Rendered and Employee Notes"
          />

          <InfoTable
            columns={COLUMNS_SERVICES_RENDERED}
            data={servicesRenderedData}
            loading={state.saving}
          />
        </>
      )}
      <SectionBar
        title="Services Rendered History"
        key={state.servicesRendered.toString() + 'History'}
        fixedActions={true}
        actions={
          loading
            ? []
            : [
                ...([
                  NO_STATUS,
                  ENROUTE,
                  ON_CALL,
                  COMPLETED,
                  INCOMPLETE,
                  //PAYMENT,
                  ADMIN,
                  SIGNED_AS,
                ].includes(lastStatus)
                  ? [
                      {
                        label: ENROUTE,
                        onClick: handleChangeStatus(ENROUTE),
                        disabled:
                          [ENROUTE, ON_CALL, ADMIN].includes(lastStatus) ||
                          state.changingStatus ||
                          archived,
                      },
                    ]
                  : []),
                ...([ENROUTE, ON_CALL].includes(lastStatus)
                  ? [
                      {
                        label: ON_CALL,
                        onClick: handleChangeStatus(ON_CALL) || state.changingStatus,
                        disabled:
                          [ON_CALL].includes(lastStatus) || state.changingStatus || archived,
                      },
                    ]
                  : []),
                ...([
                  NO_STATUS,
                  COMPLETED,
                  INCOMPLETE,
                  // PAYMENT,
                  SIGNATURE,
                  SIGNED_AS,
                ].includes(lastStatus) &&
                (loggedUser.permissionGroups.find((el) => el.name === 'ServiceCallBillingAccess') ||
                  loggedUser.permissionGroups.find((el) => el.name === 'Manager') ||
                  loggedUser.permissionGroups.find((el) => el.name === 'ServiceCallAdmin'))
                  ? [
                      {
                        label: ADMIN,
                        onClick: handleChangeStatus(ADMIN),
                        disabled: state.changingStatus || archived,
                      },
                    ]
                  : []),
                ...([ON_CALL, ADMIN].includes(lastStatus)
                  ? [
                      {
                        label: COMPLETED,
                        onClick: () => {
                          if (buttonDisabled) return;
                          setButtonDisabled(true);
                          const errors = handleCheckAmountValid(state.serviceRenderedPayment);
                          setIsRenderedPaymentValid(!errors);
                          if (!errors) {
                            const showCompleteChoiceDialog =
                              lastStatus === ADMIN && hasServiceCallAdmin;
                            if (showCompleteChoiceDialog) {
                              setAdminCompleteChoiceDialogOpen(true);
                              setButtonDisabled(false);
                            } else {
                              handleChangeStatus(COMPLETED)().finally(() =>
                                setButtonDisabled(false),
                              );
                            }
                          } else {
                            setButtonDisabled(false);
                          }
                        },
                        disabled:
                          state.changingStatus ||
                          buttonDisabled ||
                          archived ||
                          (state.serviceRenderedPayment.amountCollected
                            ? state.serviceRenderedPayment.paymentType === '-- Select --'
                            : false),
                        status: 'success' as const,
                      },
                      {
                        label: INCOMPLETE,
                        onClick: () => {
                          if (buttonDisabled) return;
                          setButtonDisabled(true);
                          const errors = handleCheckAmountValid(state.serviceRenderedPayment);
                          setIsRenderedPaymentValid(!errors);
                          if (!errors) {
                            handleChangeStatus(INCOMPLETE)().finally(() =>
                              setButtonDisabled(false),
                            );
                          } else {
                            setButtonDisabled(false);
                          }
                        },
                        disabled: state.changingStatus || buttonDisabled || archived,
                        status: 'failure' as const,
                      },
                    ]
                  : []),
                ...([NO_STATUS, ENROUTE, COMPLETED, INCOMPLETE, SIGNED_AS].includes(lastStatus)
                  ? [
                      /* {
label: PAYMENT,
onClick: handleChangeStatus(PAYMENT),
disabled: state.changingStatus,
},
*/
                      {
                        label: SIGNATURE,
                        onClick: handleChangeStatus(SIGNATURE),
                        disabled: state.changingStatus || archived,
                      },
                    ]
                  : []),
                ...([SIGNATURE /*PAYMENT*/].includes(lastStatus)
                  ? [
                      {
                        label: 'SAVE',
                        onClick: handleChangeStatus(SIGNED_AS),
                        disabled: state.changingStatus || archived,
                      },
                    ]
                  : []),
                // Always include the Flat Rates button
                {
                  label: 'FLAT RATES',
                  onClick: handleOpenFlatRates,
                  startIcon: <ListIcon />,
                  disabled: state.changingStatus || archived,
                },
              ]
        }
      />

      {/*[PAYMENT].includes(lastStatus) && (
        <PlainForm
          schema={SCHEMA_PAYMENT_AND_SIGNATURE}
          data={state.paymentForm}
          onChange={data =>
            dispatch({ type: ACTIONS.SET_PAYMENT_FORM, data: data })
          }
        />
        )*/}
      {[SIGNATURE].includes(lastStatus) && (
        <PlainForm
          {...(signatureFormErrors.length > 0 && {
            error: (
              <ul>
                {signatureFormErrors.map((error, index) => (
                  <li key={index}>
                    <strong>{error.key}</strong>
                    {error.message}
                  </li>
                ))}
              </ul>
            ),
          })}
          schema={SCHEMA_SIGNATURE}
          className="flex flex-col gap-2"
          data={state.signatureForm}
          onChange={(data) => dispatch({ type: ACTIONS.SET_SIGNATURE_FORM, data: data })}
        />
      )}

      {/* Debug information - Now removed since we confirmed state management works */}

      {[ON_CALL, ADMIN].includes(lastStatus) && (
        <div className="flex flex-col gap-2 px-4 py-2">
          <div className="flex flex-row gap-2">
            <Tooltip title="Materials/Services Editor">
              <IconButton
                className="max-w-max"
                key={'toggleInlineMaterials'}
                onClick={() => {
                  console.log(
                    'Toggle clicked. Current creatingQuote state:',
                    servicesCreatingQuote,
                  );
                  onSetServicesCreatingQuote(!servicesCreatingQuote);
                  console.log('Updated creatingQuote to:', !servicesCreatingQuote);
                }}
                size="large"
                disabled={state.saving || archived}
                color={servicesCreatingQuote ? 'primary' : 'default'}
              >
                <HomeRepairService className="!h-10 !w-10" />
              </IconButton>
            </Tooltip>
          </div>

          <PlainForm
            className="ServicesOnCallForm !p-0"
            schema={SCHEMA_ON_CALL}
            data={state.serviceRenderedPayment}
            onChange={(data) => {
              dispatch({
                type: ACTIONS.SET_SERVICE_RENDERED_PAYMENT,
                data: data,
              });
              updateServiceRenderedPayment?.(data);
            }}
            disabled={state.saving || archived}
            validations={
              !isRenderedPaymentValid
                ? handleCheckAmountValid(state.serviceRenderedPayment)
                : undefined
            }
            compact
          />

          {/* Always render, but toggle visibility with CSS */}
          <div
            className="inline-quote-selector"
            style={{
              position: 'relative',
              zIndex: 1000,
              border: '2px solid #3f51b5',
              borderRadius: '8px',
              padding: '16px',
              marginTop: '12px',
              backgroundColor: 'white',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              overflow: 'visible',
              display: servicesCreatingQuote ? 'block' : 'none',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px',
                borderBottom: '1px solid #e0e0e0',
                paddingBottom: '8px',
              }}
            >
              <h2
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  color: '#d32f2f',
                  margin: 0,
                }}
              >
                Materials/Services Editor
              </h2>
              <Button
                onClick={() => onSetServicesCreatingQuote(false)}
                variant="outline"
                size="sm"
                style={{
                  minWidth: '100px',
                  backgroundColor: 'white',
                }}
              >
                Close Editor
              </Button>
            </div>

            <div style={{ position: 'relative', height: 'auto' }}>
              <QuoteSelector
                onUpdate={onUpdateMaterials}
                archived={archived}
                pendingNewQuotableProp={state.pendingNewQuotable}
                pendingQuotableProp={state.pendingQuotable}
                setPendingNewQuotableProp={onSetServicesPendingNewQuotable}
                setPendingQuotableProp={onSetServicesPendingQuotable}
                servicesRenderedId={
                  servicesEditing.servicesRenderedId !== 0
                    ? servicesEditing.servicesRenderedId
                    : undefined
                }
                initialDepartment={department ? parseInt(department, 10) : undefined}
              />
            </div>
          </div>
        </div>
      )}
      <InfoTable
        columns={COLUMNS_SERVICES_RENDERED_HISTORY}
        key={state.servicesRendered.toString()}
        data={data}
        loading={loading}
      />

      {state.deleting && (
        <ConfirmDelete
          open
          onClose={handleDeleting()}
          kind="Service Rendered Item"
          name={`Employee: ${state.deleting.name}, Status: ${state.deleting.status}`}
          onConfirm={handleDelete}
          submitLabel={state.saving ? 'Deleting...' : 'Confirm'}
          submitDisabled={state.saving}
          disabled={state.saving}
        />
      )}
      <Dialog open={adminCompleteChoiceDialogOpen} onOpenChange={setAdminCompleteChoiceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Finish this job</DialogTitle>
          </DialogHeader>
          <div className="text-muted-foreground flex flex-col gap-2 text-sm">
            <p>
              If you would like to close out the job, press <strong>Complete</strong>.
            </p>
            <p>
              If you would like an admin to review this job later, press{' '}
              <strong>Set to Admin Review</strong>.
            </p>
          </div>
          <DialogFooter className="flex flex-col items-center justify-center gap-2 sm:flex-col">
            <Button
              className="w-full max-w-xs"
              onClick={() => {
                setAdminCompleteChoiceDialogOpen(false);
                setButtonDisabled(true);
                handleChangeStatus(COMPLETED, { forceLogJobStatus: 'Completed' })().finally(() =>
                  setButtonDisabled(false),
                );
              }}
            >
              Complete
            </Button>
            <Button
              className="w-full max-w-xs"
              variant="outline"
              onClick={() => {
                setAdminCompleteChoiceDialogOpen(false);
                setButtonDisabled(true);
                handleChangeStatus(COMPLETED, { forceLogJobStatus: 'Admin Review' })().finally(() =>
                  setButtonDisabled(false),
                );
              }}
            >
              Set to Admin Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {servicesEditing.servicesRenderedId != 0 && (
        <Modal open onClose={handleSetEditing()}>
          <div className={'ServicesEditing edit ' + servicesEditing.paymentCollected}>
            <Form<ServicesRenderedPaymentType>
              title="Services Rendered Edit"
              schema={SCHEMA_ON_CALL}
              data={servicesEditing}
              onChange={(data) => {
                setIsRenderedPaymentEditValid(!handleCheckAmountValid(data));
                onSetServicesEditing(data);
              }}
              onClose={handleSetEditing()}
              validationsProp={
                !isRenderedPaymentEditValid ? handleCheckAmountValid(servicesEditing) : undefined
              }
              submitLabel={'Save Services Rendered'}
              submitDisabled={!isRenderedPaymentEditValid}
              onSave={handleChangeServiceRendered}
              disabled={state.saving || archived}
            ></Form>
            <QuoteSelector
              archived={archived}
              servicesRenderedId={servicesEditing.servicesRenderedId}
              onUpdate={onUpdateMaterials}
            />
          </div>
        </Modal>
      )}
      {state.viewPayment != undefined && (
        <Modal open onClose={() => dispatch({ type: ACTIONS.SET_VIEW_PAYMENT, data: undefined })}>
          <div className="ServicesEditing">
            <PlainForm<PaymentType>
              schema={SCHEMA_PAYMENT}
              onChange={console.log}
              data={state.viewPayment}
              disabled={state.saving || archived}
            ></PlainForm>
          </div>
        </Modal>
      )}
      {state.viewSignature != undefined && (
        <Modal open onClose={() => dispatch({ type: ACTIONS.SET_VIEW_SIGNATURE, data: undefined })}>
          <div className="ServicesEditing with-img">
            <PlainForm<SavedSignatureType>
              schema={SCHEMA_SIGNATURE_SAVED}
              onChange={console.log}
              data={state.viewSignature}
              disabled={state.saving || archived}
            />
            <img alt="signature" src={state.viewSignature.signatureData}></img>
          </div>
        </Modal>
      )}
      {flatRateModalOpen && (
        <Modal open onClose={handleCloseFlatRates}>
          <FlatRateSheet
            onClose={handleCloseFlatRates}
            initialDepartment={department ? parseInt(department, 10) : 0}
            readOnly={!hasEditingAccess}
          />
        </Modal>
      )}
    </>
  );
};
